import { BaseFiniteScrollCarousel } from "@faire/deprecated-retailer-visitor-shared/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { FilterToggle } from "@faire/deprecated-retailer-visitor-shared/components/Filters/__internal__/FilterToggle";
import { SearchSuggestionPill } from "@faire/deprecated-retailer-visitor-shared/components/Filters/__internal__/SearchSuggestionPill";
import {
  getMultipleOptionalSelectionsFilters,
  getSortedFilterSections,
  getToggleFilters,
  selectIfSelectedAllSubOptions,
  syncToggleFilters,
  updateAllFilterKeys,
} from "@faire/deprecated-retailer-visitor-shared/components/Filters/FilterUtils";
import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import {
  FILTERS_QUERY_PARAM_KEY,
  PAGE_QUERY_PARAM_KEY,
  SEARCH_QUERY_PARAM_KEY,
  SEARCH_QUERY_REF_TYPE,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/consts";
import { splitFiltersFromQueryParamString } from "@faire/deprecated-retailer-visitor-shared/filters/splitFiltersFromQueryParams";
import { uniqueAndSorted } from "@faire/deprecated-retailer-visitor-shared/lib/uniqueAndSorted";
import { useCategoryNavigationPrefetch } from "@faire/deprecated-retailer-visitor-shared/lib/useCategoryNavigationPrefetch";
import { isLoggedInRetailerOrBrandPreview } from "@faire/marketplace-lib/serialized-data/isLoggedInRetailer";
import { useMarketStore } from "@faire/marketplace-lib/stores/events/FaireMarkets/MarketStore";
import { useMarketCurrentEvent } from "@faire/marketplace-lib/stores/events/FaireMarkets/useMarketCurrentEvent";
import { useTopOfPageContent } from "@faire/marketplace-lib/stores/ui/useTopOfPageContent";
import { Action, Core } from "@faire/slate/foundation/color";
import { isTupleOfLength } from "@faire/typescript";
import { trackLoggedOutRetailersFilterPillRowView } from "@faire/web-api--source/events/loggedOutRetailers/view/filterPillRow";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionCategoryOther/filterInteraction";
import { trackRetailersFilterClickedClick } from "@faire/web-api--source/events/retailers/click/filterClicked";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import { QueryParameters } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { getCurrentPath } from "@faire/web/common/Path";
import { LoadingSkeleton } from "@faire/web/slate-deprecated/LoadingSkeleton";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import React, { useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styled, { css } from "styled-components";

import { FilterName } from "./FilterName";

interface IProps {
  name: string;
  className?: string;
  wrap?: boolean;
  shouldShowCarouselArrows?: boolean;
}

const CATEGORY_PREFETCH_SURFACE_TYPE = "filterToggle";

const buildUrl = (
  filterSection: IFilterSection,
  queryParams: QueryParameters
) => {
  const filterOption = filterSection.options[0];
  const { filters } = queryParams;
  const normalizedFiltersQueryParams =
    (filters && isTupleOfLength(filters, 1)
      ? splitFiltersFromQueryParamString(filters[0])
      : filters) ?? [];
  const newFilters = filterOption!.is_selected
    ? normalizedFiltersQueryParams.filter(
        (filter) => filter !== filterOption!.key
      )
    : [...normalizedFiltersQueryParams, filterOption!.key!];
  const uniqueAndSortedFilters = uniqueAndSorted(newFilters);

  if (uniqueAndSortedFilters.length > 0) {
    return getCurrentPath()
      .replace({
        [FILTERS_QUERY_PARAM_KEY]: uniqueAndSortedFilters.join(","),
      })
      .delete(PAGE_QUERY_PARAM_KEY);
  } else {
    return getCurrentPath()
      .delete(FILTERS_QUERY_PARAM_KEY)
      .delete(PAGE_QUERY_PARAM_KEY);
  }
};

export const ToggleFilterRow: React.FC<IProps> = ({
  name,
  className,
  wrap = false,
  shouldShowCarouselArrows,
}) => {
  const history = useHistory();
  const {
    prefetchOnMouseEnter,
    prefetchOnTouchStart,
    clearPrefetchTimeoutOnBlur,
  } = useCategoryNavigationPrefetch(CATEGORY_PREFETCH_SURFACE_TYPE);

  const {
    data: { filterPills, filterSections, isNewFilterSelected },
    setFilterState,
  } = useFilterStore(["filterPills", "filterSections", "isNewFilterSelected"]);
  const { isDesktopAndAbove, isTabletAndAbove } = useViewport();
  const { headerHeight } = useTopOfPageContent();
  const { isLive: isMarketLive } = useMarketCurrentEvent();
  const { showAllSpecials: isMarketListLive } = useMarketStore();
  const sortedFilterSections = getSortedFilterSections(filterSections, false);
  const toggleFilters = getToggleFilters(sortedFilterSections);
  const multipleOptionalSelectionsFilters =
    getMultipleOptionalSelectionsFilters(filterSections);
  const location = useLocation();
  const queryParams = useQueryParams(QueryParameters.parse);
  const viewRef = useDoActionWhenVisible(() => {
    const joinedFilterKeys = toggleFilters
      .concat(multipleOptionalSelectionsFilters)
      .flatMap((section) => {
        return section.options.map((option) => option.key);
      })
      .join(";");
    trackLoggedOutRetailersFilterPillRowView(
      joinedFilterKeys,
      location.pathname + location.search
    );
  });
  const onMouseEnter = useCallback(
    (filterSection: IFilterSection) => () => {
      const url = buildUrl(filterSection, queryParams).toString();
      prefetchOnMouseEnter(url);
    },
    [prefetchOnMouseEnter, queryParams]
  );
  const onTouchStart = useCallback(
    (filterSection: IFilterSection) => {
      const url = buildUrl(filterSection, queryParams).toString();
      return () => prefetchOnTouchStart(url);
    },
    [prefetchOnTouchStart, queryParams]
  );

  const handleSearchQuery = useCallback(
    (eventId: string, newQuery: string, refType: string, position?: number) => {
      trackRetailersFilterClickedClick(name, eventId);
      trackRetailersFilterInteraction(
        name,
        wrap ? "toggle_filter_click_modal" : "toggle_filter_click_bar",
        eventId,
        position?.toString()
      );

      const newUrl = getCurrentPath()
        .delete(SEARCH_QUERY_REF_TYPE)
        .delete(PAGE_QUERY_PARAM_KEY)
        .replace({
          [SEARCH_QUERY_PARAM_KEY]: newQuery,
        })
        .append({
          [SEARCH_QUERY_REF_TYPE]: refType,
        })
        .toString();
      history.push(newUrl);
    },
    [history, name, wrap]
  );

  const scrollToResults = useCallback(() => {
    const window = getWindowOrThrow();
    const rowHeight = isDesktopAndAbove ? 64 : 56;
    const element = getDocumentOrThrow().getElementById(
      "search-page-results-wrapper"
    );
    const elementPos = element?.getBoundingClientRect().top ?? 0;
    const offsetPos = elementPos + window.scrollY - headerHeight - rowHeight;
    window.scrollTo?.({
      top: offsetPos,
      behavior: "smooth",
    });
  }, [headerHeight, isDesktopAndAbove]);

  const handleFilterSelected = useCallback(
    (section: IFilterSection, filterKey: string, value: boolean) => {
      trackRetailersFilterClickedClick(name, filterKey);
      trackRetailersFilterInteraction(
        name,
        wrap ? "toggle_filter_click_modal" : "toggle_filter_click_bar",
        filterKey
      );

      const sectionsUpdated = updateAllFilterKeys(
        filterSections,
        filterKey,
        value
      );
      syncToggleFilters(section, sectionsUpdated);

      if (section.field_name === IFilterSection.FieldName.TOP_CATEGORY_PILLS) {
        filterSections
          .find((s) => s.field_name === IFilterSection.FieldName.CATEGORY)
          ?.options.forEach(selectIfSelectedAllSubOptions);
      }

      setFilterState((currentState) => ({
        ...currentState,
        filterSections: [...sectionsUpdated],
        isNewFilterSelected: true,
      }));

      if (!isLoggedInRetailerOrBrandPreview()) {
        scrollToResults();
      }
    },
    [filterSections, name, scrollToResults, setFilterState, wrap]
  );

  const renderToggleFilters = useCallback(() => {
    let filterPosition = -1;

    const incrementFilterPosition = (isInvisible?: boolean) => {
      return isInvisible ? undefined : ++filterPosition;
    };

    return (
      <>
        {toggleFilters.map((filterSection: IFilterSection) => (
          <FilterToggle
            position={incrementFilterPosition(filterSection.is_invisible)}
            key={`${filterSection.display_name}`}
            name={name}
            section={filterSection}
            onChange={handleFilterSelected}
            onMouseEnter={onMouseEnter(filterSection)}
            onTouchStart={onTouchStart(filterSection)}
            onMouseLeave={clearPrefetchTimeoutOnBlur}
            disabled={isNewFilterSelected}
            isMarketLive={isMarketLive || isMarketListLive}
          />
        ))}
        {multipleOptionalSelectionsFilters.map((filterSection) =>
          filterSection.options.map((option) => (
            <FilterToggle
              position={incrementFilterPosition(filterSection.is_invisible)}
              key={`${option.key}`}
              name={name}
              section={IFilterSection.build({
                ...filterSection,
                display_name: option.display_name,
                options: [option],
              })}
              onChange={handleFilterSelected}
              disabled={isNewFilterSelected}
              isMarketLive={isMarketLive || isMarketListLive}
            />
          ))
        )}
        {filterPills?.map((filterPill) => (
          <SearchSuggestionPill
            position={incrementFilterPosition(
              filterPill.query_suggestion?.is_invisible
            )}
            name={name}
            key={`${filterPill.query_suggestion?.query_term || ""}`}
            filterPill={filterPill}
            onChange={handleSearchQuery}
            onMouseLeave={clearPrefetchTimeoutOnBlur}
            disabled={isNewFilterSelected}
          />
        ))}
      </>
    );
  }, [
    toggleFilters,
    multipleOptionalSelectionsFilters,
    filterPills,
    name,
    handleFilterSelected,
    onMouseEnter,
    onTouchStart,
    clearPrefetchTimeoutOnBlur,
    isNewFilterSelected,
    isMarketLive,
    isMarketListLive,
    handleSearchQuery,
  ]);
  const [shouldHideRightButton, setShouldHideRightButton] =
    React.useState(false);
  const [shouldHideLeftButton, setShouldHideLeftButton] = React.useState(true);
  const [isOverflow, setIsOverflow] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleResize = () => {
      if (elementRef.current) {
        setIsOverflow(
          elementRef.current.scrollWidth >
            Math.round(elementRef.current.getBoundingClientRect().width)
        );
      } else {
        setIsOverflow(false);
      }
    };

    handleResize();

    const debouncedHandleResize = debounceIfPossible(handleResize, 150);
    const window = getWindowOrThrow();
    const resizeObserver = new window.ResizeObserver(debouncedHandleResize);
    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [elementRef]);

  if (
    sortedFilterSections.length > 0 &&
    toggleFilters.length === 0 &&
    multipleOptionalSelectionsFilters.length === 0 &&
    filterPills?.length === 0
  ) {
    return null;
  }

  if (sortedFilterSections.length === 0) {
    return (
      <Row data-test-id="toggle-filter-row-placeholder">
        {Array.from({ length: 2 }, (_value, index) => (
          <SpacedLoadingSkeleton
            key={`toggleFilterPlaceholder_${index}`}
            width="140px"
            height="40px"
            borderRadius="40px"
          />
        ))}
      </Row>
    );
  }

  if (
    wrap ||
    !shouldShowCarouselArrows ||
    !isTabletAndAbove ||
    (name === FilterName.SEARCH && (!filterPills || filterPills.length === 0))
  ) {
    return (
      <Wrapper className={className} $isWrap={wrap} ref={viewRef}>
        {renderToggleFilters()}
      </Wrapper>
    );
  }

  return (
    <Wrapper $isWrap={false} className={className} ref={viewRef}>
      <StyledBaseFiniteScrollCarousel
        forwardRef={elementRef}
        name={name}
        shouldHideRightButton={!isOverflow || shouldHideRightButton}
        shouldHideLeftButton={!isOverflow || shouldHideLeftButton}
        onRightEndReached={(isRightEndReached: boolean) => {
          setShouldHideRightButton(isRightEndReached);
        }}
        onLeftEndReached={(isLeftEndReached: boolean) => {
          setShouldHideLeftButton(isLeftEndReached);
        }}
        chevronButtonDimension={{ width: 15, height: 9 }}
        useIconArrows
      >
        <Row>{renderToggleFilters()}</Row>
      </StyledBaseFiniteScrollCarousel>
    </Wrapper>
  );
};

// eslint-disable-next-line @faire/prefer-slate-attrs -- false alarm (display: none refers to scrollbar)
const Wrapper = styled(Row)<{ $isWrap: boolean }>`
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(props) =>
    props.$isWrap
      ? css`
          flex-wrap: wrap;
        `
      : css`
          overflow-x: auto;
        `}
`;

const SpacedLoadingSkeleton = styled(LoadingSkeleton)`
  margin-right: ${getSpacing("1x")};
`;

const StyledBaseFiniteScrollCarousel = styled(
  BaseFiniteScrollCarousel.BaseFiniteScrollCarousel
)`
  display: flex;
  flex-direction: row;
  top: 0;
  width: 100%;
  padding: 0;

  ${BaseFiniteScrollCarousel.Previous}, ${BaseFiniteScrollCarousel.Next} {
    color: ${Action.text.active};
    width: 30px;
    height: 30px;

    ${responsiveMediaQueries.canHover} {
      color: ${Core.text.subdued};

      &:hover {
        color: ${Action.text.hover};
      }
    }
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }

  ${BaseFiniteScrollCarousel.PreviousIcon} {
    left: 50px;
    background-color: ${Core.icon.primaryInverse};
    border-radius: 0px 50% 50% 0px;
  }

  ${BaseFiniteScrollCarousel.NextIcon} {
    right: 50px;
    background-color: ${Core.icon.primaryInverse};
    border-radius: 50% 0px 0px 50%;
  }

  ${BaseFiniteScrollCarousel.Scrollable} {
    align-items: flex-start;
    width: 100%;
  }
`;
